<template>
  <div class="page-wrapper" :class="`page-wrapper--${route.name}`">
    <nuxt-loading-indicator class="page-wrapper__progress" color="var(--color-purple)" :throttle="0" />
    <site-auth />
    <header-area />
    <parts-inline-notification v-if="messageText" :message-text="messageText" :message-type="messageType" stored-message disable-timeout />
    <nuxt-page />
    <footer-area />
    <cookie-banner />
  </div>
</template>

<script>
import HeaderArea from '~/components/HeaderArea';
import FooterArea from '~/components/FooterArea';
import CookieBanner from '~/components/CookieBanner';
import { getUserSessionData } from '~/utils/session';
import { useSessionCookies } from '~/composables/userSessionCookies';
import { checkAffiliateRefMutationHandler } from '~/hooks/mutations/affiliateMutationHandler';
import { useSearchStore } from '~/stores/searchStore';
import {
  useCartStore,
  useProductStore,
  useNotificationStore,
  useRoute,
  useRouter,
  useAudioPlayerStore,
} from '#imports';

export default {
  components: {
    HeaderArea,
    FooterArea,
    CookieBanner,
  },
  setup() {

    const { initSessionCookies } = useSessionCookies()
    initSessionCookies()

    const router = useRouter()
    const searchStore = useSearchStore()
    const clearQuery = async () => {
      await router.push('/');
      searchStore.removeAllFilters();
    }

    return {
      cartStore: useCartStore(),
      productStore: useProductStore(),
      notificationStore: useNotificationStore(),
      route: useRoute(),
      audioPlayerStore: useAudioPlayerStore(),
      clearQuery,
    }
  },
  provide() {
    return {
      clearQuery: this.clearQuery
    }
  },
  async mounted () {
    if (getUserSessionData()) {
      this.productStore.getWishlists(true).then(() => null);
    }

    window.addEventListener('click', this.handleGlobalClick)
    document.addEventListener('visibilitychange', this.handleVisibility)
    window.addEventListener('beforeunload', this.handleBeforeUnload)

    if (this.$route.query['add-to-cart']) {
      try {
        await this.cartStore.addCartItems(this.$route.query['add-to-cart'].split(',').map((id) => parseInt(id, 10)))

        this.messageText = 'Cart Updated'
        this.messageType = 'success'
      } catch (error) {
        let errorText = ''
        if (error.length) {
          errorText = error[0].message;
        } else if (error.message) {
          errorText = error.message
        }

        this.messageText = errorText
        this.messageType = 'error'
      }
    }

    if (this.$route.query['ref']) {
      checkAffiliateRefMutationHandler({ refToken: this.$route.query['ref'] })
    }
  },
  data() {
    return {
      messageText: '',
      messageType: '',
    }
  },
  computed: {
    latestNotification() {
      return this.notificationStore.getNotification
    }
  },
  methods: {
    handleGlobalClick(event) {
      const target = event.target.closest('a')

      if (target && target.href && target.dataset?.product_id) {
        const url = new URL(target.href)
        if (url.searchParams.has('add-to-cart')) {
          event.preventDefault()

          const productId = target.dataset.product_id

          this.addToCart(productId)
        }
      }
    },
    async addToCart(productId) {
      await this.cartStore.addToCart(productId,  1)
    },
    handleVisibility() {
      if (document.hidden) {
        this.audioPlayerStore.pauseOnly()
      }
    },
    handleBeforeUnload() {
      this.audioPlayerStore.stopPlayback(false, true)
    },
  },
  beforeUnmount() {
    document.removeEventListener('visibilitychange', this.handleVisibility)
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  },
  watch: {
    'route.fullPath'(to, from) {
      setTimeout(() => {
        const wasPlaying = this.audioPlayerStore?.isPlaying;
        const loadedProduct = this.audioPlayerStore?.loadedProductDetails;

        if (!loadedProduct) {
          return;
        }

        const toPath = to.split('?')[0];
        const toSlug = toPath.split('/').filter(Boolean).pop();
        const productLink = loadedProduct.productLink?.split('?')[0].split('/').filter(Boolean).pop();
        const isSameProduct = toSlug === productLink;

        /* Working Cantrip for Debugging Route Changes */
        /**
        console.log('XY - ROUTE CHANGE DEBUG:', {
          from,
          to,
          toSlug,
          productLink,
          wasPlaying,
          isSameProduct,
          willStop: !isSameProduct
        });
        **/

        if (!isSameProduct && !wasPlaying) {
          this.audioPlayerStore?.stopPlayback(true);
        } else if (!isSameProduct && wasPlaying) {
          this.audioPlayerStore?.stopPlayback(true);
        }
      }, 20);
    },
    latestNotification: {
      handler(newNotification) {
        this.messageText = newNotification.messageText
        this.messageType = newNotification.messageType
      },
      deep: true,
    },
  }
}
</script>

<style lang="scss">
@use 'sass:math';
@use '~/common/scss/variables';
@use '~/common/scss/helpers';
@use '~/common/scss/container';
@use '~/common/scss/typography';
@use "ress/ress.css";
@use '~/common/scss/legacy_wp';

:root {
  --primary-font: #{variables.$primaryFont};
  --secondary-font: 'Helvetica Neue', Arial;

  --color-black: #{variables.$colorBlack};
  --color-white: #{variables.$colorWhite};

  --color-yellow: #{variables.$colorYellow};
  --color-orange: #{variables.$colorOrange};
  --color-purple: #{variables.$colorPurple};

  // Overwrite wp gutenberg css variables
  --wp--preset--font-family--body: var(--primary-font) !important;
  --wp--preset--font-family--heading: var(--primary-font) !important;

  --wp--preset--color--base: var(--color-white) !important;

  font-family: var(--primary-font);
  font-size: math.div(14, 16) * 100% !important; // overwrite wp global styles

  scroll-padding-top: 70px;
}

input,
textarea {
  max-width: 100%;
}

svg {
  height: 1.25em;
  width: 1.1em;
}

// FAQ toggle items using native html elements
details {
  @include container.m();

  border-radius: 4px;
  margin-left: auto !important; // need to overwrite global styles
  margin-right: auto !important;
  padding: .5rem .5rem;
  transition: background-color .25s;

  &[open] {
    background-color: variables.$colorGreyED;

    summary::before {
      background-image: url('/assets/icons/IconMinusCircle.svg');
      content: "";
    }
  }

  summary {
    align-items: center;
    display: flex;
    justify-content: center;
    list-style: none;
    width: 100%;

    &::before {
      background-image: url('/assets/icons/IconPlusCircle.svg');
      content: "";
      height: helpers.px-to-rem(20);
      margin-right: .75rem;
      width: helpers.px-to-rem(20);
    }

    font-size: helpers.px-to-rem(20);
  }

  p {
    --overlay-width: 100%;

    padding: .5rem 1rem 1rem 1.75rem;
    width: 100%;
  }
}
</style>